<script lang="ts">
  import type { PricingPlanCard, PwNextPricingPlanCardsProps } from './types';
  import { Link, Text, Badge } from '@squareup/public-web-design-system';
  import { isUserLoggedIn } from '@squareup/public-web-browser-helpers';
  import { planGroupPricing, setPlanGroupPricing } from '$lib/helpers/pricing-data';
  import { getContext, onMount } from 'svelte';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { isValidUrl } from '$lib/helpers/browser/utils';
  import DetailAccordion from './components/detail-accordion.svelte';
  import DetailTooltip from './components/tooltip.svelte';
  import layoutHelpers from '$lib/helpers/layout-helpers';

  interface ProcessingText {
    name?: string;
    rate?: string;
  }

  const component = 'PwNextPricingPlanCards';

  // #region Props
  export let entryId: PwNextPricingPlanCardsProps['entryId'];
  export let customCssClass: PwNextPricingPlanCardsProps['customCssClass'];
  export let cards: PwNextPricingPlanCardsProps['cards'];
  export let hasDropShadow: PwNextPricingPlanCardsProps['hasDropShadow'];
  export let apiErrorRedirectUrl: PwNextPricingPlanCardsProps['apiErrorRedirectUrl'];
  export let customPricingCard: PwNextPricingPlanCardsProps['customPricingCard'];
  // #endregion

  // #region Setup
  let priceTextSize: '600' | '700' | '800' = '600';
  let detailTextSize: '100' | '50-r';
  let loggedInState: boolean = false;
  const locale: string = getContext('locale');
  const rowCount = cards.every((card) => !card.details) ? 3 : 4;
  // #endregion

  // #region API Data
  $: isApiLoading = $planGroupPricing.ok === null;
  $: if ($planGroupPricing.ok) {
    cards.forEach((card, i) => {
      card.title.name = $planGroupPricing?.plan_group?.plans?.[i]?.name;
      card.title.description = $planGroupPricing?.plan_group?.plans?.[i]?.description;
      card.cost.amount = $planGroupPricing?.plan_group?.plans?.[i]?.localized_headline_plan_pricing?.localized_amount;
      card.cost.recurrency =
        $planGroupPricing?.plan_group?.plans?.[i]?.localized_headline_plan_pricing?.localized_recurrency_text;
    });
    cards = [...cards]; // trigger reactivity
  }
  /// / TODO: check what we expect to happen if API fails
  /// / Currently sets component to display: none and optionally redirects to an error page
  else if ($planGroupPricing.ok === false) {
    if (apiErrorRedirectUrl?.length && isValidUrl(apiErrorRedirectUrl, { relative: true })) {
      window.location.href = apiErrorRedirectUrl as string;
    } else {
      console.error('Invalid URL. Only relative URLs are allowed. Could not redirect to', apiErrorRedirectUrl);
    }
  }

  function getProcessingText(lookupKey: string | undefined, i: number): ProcessingText | undefined {
    if (!lookupKey) {
      return undefined;
    }
    const rateObject = $planGroupPricing?.plan_group?.plans?.[i]?.headline_fee_rates.find(
      (v: any) => v.headline_rate_name === lookupKey
    );
    return {
      name: rateObject?.localized_headline_rate_name,
      rate: rateObject?.localized_base_rate_display,
    };
  }

  function getCtaText(card: PricingPlanCard, i: number) {
    /// / TODO: is there a better way to make this reactive?
    const numFreeTrialDays = $planGroupPricing?.plan_group?.plans?.[i]?.num_free_trial_days;
    const ctaText = card.cta.props.text;

    if (numFreeTrialDays && ctaText.includes('{{sub2_api.num_free_trial_days}}')) {
      return ctaText.replace('{{sub2_api.num_free_trial_days}}', numFreeTrialDays);
    }
    return ctaText;
  }
  // #endregion

  // dynamic price text sizing by screen width
  function onResize() {
    if (window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.medium.minWidth) {
      priceTextSize = '800';
      detailTextSize = '100';
    } else if (window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.large.minWidth) {
      priceTextSize = '700';
      detailTextSize = '50-r';
    } else {
      priceTextSize = '600';
      detailTextSize = '50-r';
    }
  }

  function getCardCtaUrl(loggedIn: boolean, card: PricingPlanCard): string {
    // check if user is logged in or not
    if (loggedIn && card.details?.loggedInCtaUrl) {
      return card.details?.loggedInCtaUrl;
    }
    return card.cta.props.url;
  }

  // #region Lifecycle
  onMount(async () => {
    onResize();
    setPlanGroupPricing(locale);
    loggedInState = await isUserLoggedIn();
  });
  // #endregion
</script>

<svelte:window on:resize={onResize} />
<div
  class="{component} {component}-{entryId}-box-properties {customCssClass}"
  data-entry-id={entryId}
  class:api-error={$planGroupPricing.ok === false}
  style={`--rowCount: ${rowCount}`}
>
  {#each cards as card, i}
    <div
      id="pricing-plan-card-{card.tier}"
      class="{component}-card"
      class:has-details={card.details?.heading}
      class:dropshadow={hasDropShadow}
    >
      <!-- Title Section -->
      <div class="{component}-card-title" class:loading={isApiLoading}>
        <div class:hasBadge={card.badge}>
          <Text as="h2" variant="200">{card.title.name}</Text>
          {#if card.badge}
            <Badge>{card.badge}</Badge>
          {/if}
        </div>
        <div class:loading={isApiLoading}>
          <div class="text--color-gray">
            <Text variant="100">{card.title.description}</Text>
          </div>
        </div>
      </div>

      <!-- Fixed/Flexible Toggle will go here -->

      <!-- Cost Section -->
      <div class="{component}-card-cost" class:loading={isApiLoading}>
        <Text variant={priceTextSize}>{card.cost.amount}</Text>
        <div class="text--color-gray cost-recurrency">
          <Text variant="100">{card.cost.recurrency}</Text>
        </div>
      </div>

      <!-- CTA -->
      <div class="{component}-card-cta" class:loading={isApiLoading} class:logged-in-url={loggedInState}>
        <Link variant={card.cta.props.type} text={getCtaText(card, i)} href={getCardCtaUrl(loggedInState, card)} />
      </div>

      <!-- Details Section -->
      {#if card.details?.heading}
        <div class="{component}-card-details">
          {#each card.details.sections as section}
            {@const hasTooltips = section.items.some((item) => item.tooltip)}
            <div class="{component}-card-details--desktop">
              <div class="{component}-card-details-section">
                <div class:loading={isApiLoading}>
                  <Text variant="100">{section.heading}</Text>
                </div>
                {#each section.items as item}
                  <div
                    class="{component}-card-details-section-item"
                    class:processing={section.type === 'processing'}
                    class:tooltip={item.tooltip}
                    class:tooltip--padding={hasTooltips}
                    class:loading={isApiLoading}
                  >
                    {#if item.tooltip}
                      <DetailTooltip title={item.text} tooltip={item.tooltip} />
                    {/if}

                    <!-- Processing Details -->
                    {#if section.type === 'processing'}
                      {@const rate = getProcessingText(item.lookupKey, i)}
                      <div class="text--weight-regular">
                        <Text variant={detailTextSize}>{rate?.name}</Text>
                      </div>
                      <Text variant={detailTextSize}>{rate?.rate}</Text>

                      <!-- Regular Details -->
                    {:else}
                      <DynamicChildrenComponent children={[item.icon]} />
                      <div class="text--weight-regular">
                        <Text variant={detailTextSize}>{@html item.text}</Text>
                      </div>
                    {/if}
                  </div>
                {/each}

                {#if section.link}
                  <div class="{component}-card-details-section-link" class:loading={isApiLoading}>
                    <DynamicChildrenComponent children={[section.link]} />
                  </div>
                {/if}
              </div>
            </div>
          {/each}
          <!-- Details Section Mobile -->
          <DetailAccordion title={card.details.heading}>
            {#each card.details.sections as section}
              {@const hasTooltips = section.items.some((item) => item.tooltip)}
              <div class="{component}-card-details-section">
                <Text variant="100">{section.heading}</Text>
                {#each section.items as item}
                  <div
                    class="{component}-card-details-section-item"
                    class:processing={section.type === 'processing'}
                    class:tooltip={item.tooltip}
                    class:tooltip--padding={hasTooltips}
                    class:loading={isApiLoading}
                  >
                    {#if item.tooltip}
                      <DetailTooltip title={item.text} tooltip={item.tooltip} />
                    {/if}

                    <!-- Processing Details -->
                    {#if section.type === 'processing'}
                      {@const rate = getProcessingText(item.lookupKey, i)}
                      <div class="text--weight-regular">
                        <Text variant={detailTextSize}>{rate?.name}</Text>
                      </div>
                      <Text variant={detailTextSize}>{rate?.rate}</Text>

                      <!-- Regular Details -->
                    {:else}
                      <DynamicChildrenComponent children={[item.icon]} />
                      <div class="text--weight-regular">
                        <Text variant={detailTextSize}>{@html item.text}</Text>
                      </div>
                    {/if}
                  </div>
                {/each}

                {#if section.link}
                  <div class="{component}-card-details-section-link" class:loading={isApiLoading}>
                    <DynamicChildrenComponent children={[section.link]} />
                  </div>
                {/if}
              </div>
            {/each}
          </DetailAccordion>
        </div>
      {/if}
    </div>
  {/each}
  {#if customPricingCard?.headline && customPricingCard?.copy}
    <div class="PwNextPricingPlanCards-custom-pricing" class:dropshadow={hasDropShadow}>
      <div class="PwNextPricingPlanCards-custom-pricing-content" class:loading={isApiLoading}>
        <DynamicChildrenComponent children={[customPricingCard?.headline]} />
        <DynamicChildrenComponent children={[customPricingCard?.copy]} />
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;
  @use '@squareup/public-web-design-system/dist/components/Action/action' as actionUtils;

  @layer custom-components {
    :global {
      .Hero {
        &:has(.PwNextPricingPlanCards) {
          .TextStack {
            span,
            p {
              --color: var(--color-white);
            }
          }
          .Hero__content {
            overflow: visible;
          }
        }
      }
    }

    .PwNextPricingPlanCards-custom-pricing-content {
      @include mediaQueries.at('large') {
        :global(.sq-custom-pwds-700) {
          --font-size: 3.8rem; // force 700 text to stay this size on large screens
        }
      }
    }

    .PwNextPricingPlanCards {
      display: grid;
      grid-template-columns: 1fr;
      gap: var(--space-20);

      max-width: var(--container-width-current);
      margin: auto;

      @include mediaQueries.at('medium') {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      // special button sizing to let mobile screens have 'regular' buttons
      // but tablet screens have 'small' buttons
      @include mediaQueries.at('small') {
        :global {
          .Action--button.Action--regular,
          .Action--button-alt.Action--regular {
            .Action__content-wrapper {
              --action-variant-button-padding: var(--action-variant-button-size-small-padding);
              --action-variant-button-font-size: var(--action-size-small-font-size);
            }
          }
        }
      }

      &.api-error {
        display: none;
      }

      .cost-recurrency {
        margin-top: var(--space-10);
      }

      &-card {
        display: grid;
        grid-template-rows: subgrid;
        grid-template-areas:
          'title'
          'cost'
          'cta';
        gap: var(--space-40);
        grid-row: span var(--rowCount);
        padding: var(--space-20);
        background-color: var(--color-white);
        border-radius: var(--container-border-radius);

        @include mediaQueries.at('large') {
          gap: var(--space-40);
          padding: var(--space-40);
        }

        @include mediaQueries.until('small') {
          grid-template-rows: 1fr;
        }

        .hasBadge {
          display: flex;
          justify-content: space-between;
          align-items: center;
          --badge-border-radius: 10px;
          --badge-padding: var(--space-10);
        }

        &.has-details {
          grid-template-areas:
            'title'
            'cost'
            'cta'
            'details';
        }

        &.dropshadow {
          box-shadow: 5px 5px 50px 0px rgba(0, 0, 0, 0.1);
        }

        &-title {
          display: flex;
          flex-direction: column;
          gap: var(--space-10);
          grid-area: title;
        }

        &-cta {
          grid-area: cta;

          // prevent the from wrapping when loading
          &.loading {
            overflow: hidden;
            :global {
              .Action__content {
                white-space: nowrap;
              }
            }
          }

          :global {
            & > {
              @include actionUtils.full-width();
            }
          }
        }

        &-details {
          grid-area: details;
          display: flex;
          flex-direction: column;
          gap: var(--space-40);

          &--desktop {
            display: block;

            @include mediaQueries.until('medium') {
              display: none;
            }
          }

          &-section {
            display: flex;
            flex-direction: column;
            gap: var(--space-10);

            &-item {
              display: flex;
              align-items: center;
              gap: var(--space-10);
              border-radius: var(--container-border-radius);

              &.processing {
                justify-content: space-between;

                :global(> .Text) {
                  min-width: var(--space-100); // keeps processing fee values from wrapping
                  text-align: right;
                }
              }

              &.tooltip {
                cursor: pointer;
                background-color: var(--color-neutral-900);
                transition: background-color 0.3s ease-in-out;
                position: relative;

                &--padding {
                  padding: var(--space-10);
                }

                &:hover {
                  background-color: var(--color-neutral-800);

                  :global(.tooltip) {
                    opacity: 1;
                    visibility: visible;
                  }
                }
              }

              &:not(.tooltip) {
                align-items: start;
              }

              :global(.PwNextIcon) {
                margin-top: -0.3rem; // vertically center icons
              }
            }

            &-link {
              margin-top: var(--space-20);
            }
          }
        }

        :global(.HorizontalRule) {
          min-width: unset;
          grid-area: divider;
        }
      }

      .text {
        &--color-gray {
          :global {
            & > * {
              color: var(--color-neutral-400);
            }
          }
        }

        &--weight-regular {
          :global {
            & > * {
              font-weight: var(--font-weight-regular);
            }
          }
        }
      }

      .loading {
        position: relative;
        &:after {
          content: '';

          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;

          border-radius: var(--container-border-radius);

          background: linear-gradient(
            to right,
            var(--color-neutral-900) 30%,
            var(--color-neutral-800) 50%,
            var(--color-neutral-900) 70%
          );
          background-size: 300%;
          background-position-x: 100%;
          animation: shimmer 1s infinite linear;
        }

        :global {
          & > * {
            visibility: hidden;
          }
        }
      }

      @keyframes shimmer {
        to {
          background-position-x: 0%;
        }
      }

      &-custom-pricing {
        padding: var(--space-20);
        background-color: var(--color-white);
        border-radius: var(--container-border-radius);
        order: 2; // force it to be the last item;

        &.dropshadow {
          box-shadow: 5px 5px 50px 0px rgba(0, 0, 0, 0.1);
        }

        @include mediaQueries.at('medium') {
          grid-area: calc(var(--rowCount) + 1) / span 3;
        }

        :global {
          & > * {
            display: grid;
            grid-template-columns: 1fr;
            gap: var(--space-20);

            @include mediaQueries.at('large') {
              grid-template-columns: 0.9fr 2fr;
              grid-column: span 2;
              gap: var(--space-40);
            }

            @include mediaQueries.until('medium') {
              .LinkComponent {
                width: 100% !important;
                max-width: 100% !important;
              }

              @include actionUtils.full-width();
            }

            .FlexLayoutSection {
              width: 100%;

              span {
                max-width: 100%;
              }
            }
          }
        }

        @include mediaQueries.at('large') {
          padding: var(--space-40);
        }
      }
    }
  }
</style>
